import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/common/header";
import Footer from "../components/common/footer";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Radio,
  Shield,
  TrendingUp,
  Wallet,
  XCircle,
  Zap,
} from "lucide-react";

const STATE_URL = "/auto-trader-state.json";
const WS_URL = "wss://cryptospace.network/wallet-feed";

const EVENT_LABELS = {
  REAL_SWAP: "Swap detected",
  LARGE_VALUE_TX: "Large movement",
  FIRST_TOKEN_ACTIVITY: "New token activity",
  NEW_CONTRACT_DEPLOY: "New contract",
  BOT_LIKE_TX: "Bot-like behavior",
  GAS_SPIKE: "Gas spike",
  COMPLEX_TX: "Complex transaction",
};

function age(value) {
  if (!value) return "waiting";
  const diff = Date.now() - new Date(value).getTime();
  if (Number.isNaN(diff)) return "waiting";
  if (diff < 0) return "now";
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function money(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function short(value) {
  if (!value) return "unknown";
  return value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-5)}` : value;
}

function formatNetwork(value) {
  if (!value) return "unknown";
  if (value === "bnb") return "BNB";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function actionForSignal(signal) {
  const score = Number(signal.score || signal.entryScore || 0);
  const event = signal.event || signal.entryEvent || "";
  if (score >= 70) return "Test candidate";
  if (event === "REAL_SWAP") return "Watch route";
  if (event.includes("CONTRACT")) return "Risk review";
  return "Monitor";
}

function modeLabel(mode) {
  return mode === "paper" || !mode ? "TEST MODE" : String(mode).toUpperCase();
}

export default function LiveSignalCenterPage() {
  const [state, setState] = useState(null);
  const [liveSignals, setLiveSignals] = useState([]);
  const [wsStatus, setWsStatus] = useState("connecting");
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;

    async function loadState() {
      try {
        const response = await fetch(`${STATE_URL}?t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!response.ok) throw new Error("state unavailable");
        const next = await response.json();
        if (!cancelled) {
          setState(next);
        }
      } catch {
        // keep the live websocket visible even while public state is waiting
      }
    }

    loadState();
    const timer = window.setInterval(loadState, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const ws = new WebSocket(WS_URL);
    ws.onopen = () => setWsStatus("connected");
    ws.onerror = () => setWsStatus("waiting");
    ws.onclose = () => setWsStatus("closed");
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        setLiveSignals((prev) => [
          { ...message, receivedAt: new Date().toISOString() },
          ...prev,
        ].slice(0, 8));
      } catch {
        // ignore malformed live messages
      }
    };

    return () => ws.close();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const openPositions = useMemo(() => state?.positions || [], [state]);
  const rejected = useMemo(() => state?.rejectedSignals || [], [state]);
  const closed = useMemo(() => state?.closedTrades || [], [state]);

  const activeNetworks = useMemo(() => {
    const nets = new Set([
      ...openPositions.map((item) => item.net),
      ...rejected.map((item) => item.net),
      ...closed.map((item) => item.net),
    ].filter(Boolean));
    return Array.from(nets).slice(0, 6);
  }, [openPositions, rejected, closed]);

  const nowEvents = useMemo(() => {
    const testPositions = openPositions.slice(0, 4).map((item) => ({
      type: "Test position",
      token: item.tokenSymbol || short(item.tokenAddress),
      net: item.net,
      score: item.entryScore,
      action: "Test watch",
      reason: item.entryReasons?.join(", ") || item.market?.dexId || "route tracked",
      age: item.openedAt,
      tone: "good",
    }));

    const live = liveSignals.slice(0, 4).map((item) => ({
      type: EVENT_LABELS[item.event] || item.event || "Live signal",
      token: item.tokenSymbol || short(item.tokenAddress || item.contract || item.txHash),
      net: item.net,
      score: item.score,
      action: actionForSignal(item),
      reason: item.reason || item.event || "live feed",
      age: item.receivedAt,
      tone: "live",
    }));

    return [...live, ...testPositions].slice(0, 6);
  }, [liveSignals, openPositions]);

  const latestSignalAt = liveSignals[0]?.receivedAt || state?.health?.lastSignalAt;
  return (
    <div className="signal-center-page">
      <Header />
      <main className="signal-center-shell">
        <section className="signal-center-hero">
          <div>
            <div className="signal-center-kicker">
              <Radio size={16} />
              Live signal center
            </div>
            <h1>What ChainIntel is seeing right now.</h1>
            <p>
              A readable layer over live network signals, test-trading
              decisions, and risk blocks. Raw logs stay available, but this page
              shows what the system is doing with the feed.
            </p>
            <div className="signal-center-actions">
              <Link to="/auto-trading">Open Auto Trader</Link>
              <Link to="/feed">View Raw Logs</Link>
              <Link to="/dashboard">Open Market Tracker</Link>
            </div>
          </div>
          <div className="signal-live-card">
            <span className={wsStatus === "connected" ? "live-dot" : "live-dot waiting"} />
            <small>Feed</small>
            <strong>{wsStatus}</strong>
            <span>last signal {age(latestSignalAt)}</span>
          </div>
        </section>

        <section className="signal-center-stats">
          <Metric icon={<Activity />} label="Signals processed" value={state?.stats?.signals || liveSignals.length || 0} />
          <Metric icon={<TrendingUp />} label="Test entries" value={state?.stats?.entries || openPositions.length || 0} />
          <Metric icon={<Wallet />} label="Open test positions" value={openPositions.length} />
          <Metric icon={<XCircle />} label="Risk blocks" value={state?.stats?.rejected || rejected.length || 0} />
          <Metric icon={<Shield />} label="Mode" value={modeLabel(state?.mode)} />
          <Metric icon={<Clock />} label="Updated" value={age(state?.updatedAt || now)} />
        </section>

        <section className="signal-center-grid">
          <Panel title="Happening Now" icon={<Zap />}>
            {nowEvents.length ? (
              <div className="signal-event-list">
                {nowEvents.map((event, index) => (
                  <EventRow event={event} key={`${event.type}-${event.token}-${index}`} />
                ))}
              </div>
            ) : (
              <Empty text="Waiting for live signals and test decisions." />
            )}
          </Panel>

          <Panel title="Decision Flow" icon={<CheckCircle2 />}>
            <div className="signal-flow">
              <FlowStep title="Signal" text="Network event arrives from the live feed." />
              <FlowStep title="Risk Check" text="Liquidity, route, honeypot, and limits are checked." />
              <FlowStep title="Test Decision" text="The bot watches, enters, exits, or rejects." />
              <FlowStep title="Test Wallet" text="Manual test execution stays separate from live mode." />
              <FlowStep title="Live Locked" text="Real execution remains locked until review." />
            </div>
          </Panel>
        </section>

        <section className="signal-center-grid">
          <Panel title="Test Opportunities" icon={<TrendingUp />}>
            {openPositions.length ? (
              <div className="signal-card-list">
                {openPositions.slice(0, 6).map((position) => (
                  <PositionCard position={position} key={position.id} />
                ))}
              </div>
            ) : (
              <Empty text="No test opportunities open right now." />
            )}
          </Panel>

          <Panel title="Rejected Risks" icon={<AlertTriangle />}>
            {rejected.length ? (
              <div className="signal-card-list">
                {rejected.slice(0, 6).map((item, index) => (
                  <RiskCard item={item} key={`${item.txHash || item.reason}-${index}`} />
                ))}
              </div>
            ) : (
              <Empty text="No current risk blocks in the public state." />
            )}
          </Panel>
        </section>

        <section className="signal-center-network-band">
          <div>
            <h2>Active networks</h2>
            <p>
              The center follows the same networks used by the test execution
              engine, so the signal feed connects directly to bot validation.
            </p>
          </div>
          <div className="signal-network-tags">
            {(activeNetworks.length ? activeNetworks : ["ethereum", "base", "arbitrum", "polygon", "bnb", "optimism"]).map((net) => (
              <span key={net}>{formatNetwork(net)}</span>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function Metric({ icon, label, value }) {
  return (
    <div className="signal-metric">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Panel({ title, icon, children }) {
  return (
    <section className="signal-panel">
      <div className="signal-panel-title">
        {icon}
        <h2>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function EventRow({ event }) {
  return (
    <div className={`signal-event-row ${event.tone}`}>
      <div>
        <strong>{event.type}</strong>
        <span>{formatNetwork(event.net)} / {event.token}</span>
      </div>
      <div>
        <b>{event.action}</b>
        <small>{event.reason}</small>
      </div>
      <em>{event.score ? `score ${event.score}` : age(event.age)}</em>
    </div>
  );
}

function FlowStep({ title, text }) {
  return (
    <div className="signal-flow-step">
      <strong>{title}</strong>
      <span>{text}</span>
    </div>
  );
}

function PositionCard({ position }) {
  return (
    <div className="signal-mini-card">
      <div>
        <strong>{position.tokenSymbol || short(position.tokenAddress)}</strong>
        <span>{formatNetwork(position.net)} / {position.market?.dexId || "route"}</span>
      </div>
      <div>
        <b>{money(position.sizeUsd)}</b>
        <span>{position.entryScore ? `score ${position.entryScore}` : "test"}</span>
      </div>
      <small>{position.entryReasons?.join(" | ") || `opened ${age(position.openedAt)}`}</small>
    </div>
  );
}

function RiskCard({ item }) {
  return (
    <div className="signal-mini-card warning">
      <div>
        <strong>{item.reason || "Risk blocked"}</strong>
        <span>{formatNetwork(item.net)} / {item.event || "signal"}</span>
      </div>
      <div>
        <b>{item.score || "hold"}</b>
        <span>{age(item.receivedAt || item.createdAt)}</span>
      </div>
      <small>{short(item.txHash || item.tokenAddress || item.contract)}</small>
    </div>
  );
}

function Empty({ text }) {
  return <div className="signal-empty">{text}</div>;
}
